import EverCookie from 'EverCookie';
import {
  logger,
  getMarketingData,
  getApiEntrypoint,
  getCookie,
  getUserVideoInput,
  collectRiskSealSessionId,
} from 'utils';
import type { CreateUserData, UserPhone } from 'interfaces';
import {
  KEY_REFERRAL_CODE,
  KEY_APPSFLYER_UID,
  KEY_APPSFLYER_SOURCE_PLATFORM,
  KEY_STORAGE_TARGET_URL,
} from '../constants';
import { fetchUnguarded, refreshFetch, statusCodeHelperResponse } from './axios';
import { withOtpEnvelopeFields } from 'pages/Login/utils/otpRemainingStorage';
import { type Sender } from 'interfaces/connectionType';

export const sendUserPhone = async (data: UserPhone) => {
  const axiosUnguarded = fetchUnguarded();
  const EC = new EverCookie('EverCookie');
  const riskSealSessionId = await collectRiskSealSessionId();
  const fingerprint = window.fingerPrint || {};
  const marketingData = getMarketingData();
  const hasCamera = await getUserVideoInput();
  const payload: CreateUserData = {
    otp_sender_channel: data.sender ?? 'sms',
    ...(data.type === 'auth' && {
      phone_number: data.phone_number,
      has_camera: hasCamera,
      user_additional_data: {
        fingerprint_js: fingerprint,
        evercookie: EC.getItem(false, 'userID') || null,
        ...(riskSealSessionId ? { riskseal_session_id: riskSealSessionId } : {}),
        ...marketingData,
      },
    }),
    ...(data.type === 'resend' && {
      session_uuid: data.session_uuid,
      user_uuid: data.user_uuid,
    }),
  };

  if (data.type === 'auth') {
    const targetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
    if (targetUrl) {
      payload.target_url = targetUrl;
    }

    const referralCode = getCookie(KEY_REFERRAL_CODE) || null;
    if (referralCode) {
      payload.parent_referral_code = referralCode;
    }

    const appsflyerUid = getCookie(KEY_APPSFLYER_UID) || null;
    if (appsflyerUid) {
      payload.appsflyer_uid = appsflyerUid.toString();
    }

    const appsflyerPlatform = getCookie(KEY_APPSFLYER_SOURCE_PLATFORM) || null;
    if (appsflyerPlatform) {
      payload.appsflyer_source_platform = appsflyerPlatform.toString();
    }

    const firebaseToken = getCookie('firebase_token') || null;
    if (firebaseToken) {
      payload.firebase_token = String(firebaseToken);
    }
  }

  const endpoint = data.type === 'resend' ? '/auth/v1/resend-otp' : '/auth/v1/request-otp';

  const response: any = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('sendUserPhone response', response);

  const statusData = await statusCodeHelperResponse(response);

  return withOtpEnvelopeFields(statusData, response);
};

export const sendCallRequest = async (
  data: Pick<UserPhone, 'is_allow_to_receive_marketing_email' | 'phone_number' | 'session_uuid'>,
) => {
  const hasCamera = await getUserVideoInput();
  const payload = {
    otp_sender_channel: 'call-verification',
    has_camera: hasCamera,
    ...data,
  };
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/ask-call';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('sendCallRequest response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const getCallStatus = async (data: any) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/check-call-status';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data,
  });
  logger('getCallStatus response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const sendChooseOriginalPhone = async (sender: Sender = 'sms') => {
  const axios = refreshFetch();

  const response = await axios({
    method: 'post',
    url: 'fo/v1/duplicate/origin',
    data: { otp_sender_channel: sender },
  });
  logger('sendChooseOriginalPhone response', response);
  const statusData = await statusCodeHelperResponse(response);

  return withOtpEnvelopeFields(statusData, response);
};

export const sendChooseCurrentPhone = async () => {
  const axios = refreshFetch();

  const response = await axios({
    method: 'post',
    url: 'fo/v1/duplicate',
  });
  logger('sendChooseCurrentPhone response', response);
  return response;
};

export const sendOtp = async (data: { confirmation_code: string; session_uuid: string }) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/confirm-otp';
  const targetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
  const payload = {
    ...data,
    ...(targetUrl ? { target_url: targetUrl } : {}),
  };
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('sendOtp response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const sendOtpDuplicateUser = async (data: { confirmation_code: string }) => {
  const axios = refreshFetch();
  const endpoint = 'fo/v1/duplicate/confirm-otp';
  const targetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
  const payload = {
    ...data,
    ...(targetUrl ? { target_url: targetUrl } : {}),
  };
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('sendOtpDuplicateUser response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const deletePrevUser = async () => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/user';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'delete',
    url: endpoint,
  });
  logger('deletePrevUser response', response);

  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const setPincode = async (pincode: string) => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/pin_code';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: { pin_code: pincode },
  });
  logger('setPincode response', response);
  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const changePincode = async (payload: any) => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/pin_code';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'patch',
    url: endpoint,
    data: payload,
  });
  logger('changePincode response', response);
  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const loginPincode = async (payload: any) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/confirm-pin';
  const targetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
  const body = {
    ...payload,
    ...(targetUrl ? { target_url: targetUrl } : {}),
  };
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: body,
  });
  logger('loginPincode response', response);
  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const getRequestOtpForPin = async (payload: any) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/request-pin-code-otp';
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: payload,
  });
  logger('getRequestOtpForPin response', response);
  const statusData = await statusCodeHelperResponse(response);

  return withOtpEnvelopeFields(statusData, response);
};

export const confirmOtpPinOtp = async (payload: any) => {
  const axiosUnguarded = fetchUnguarded();
  const endpoint = '/auth/v1/confirm-pin-code-otp';
  const targetUrl = getCookie(KEY_STORAGE_TARGET_URL) || null;
  const body = {
    ...payload,
    ...(targetUrl ? { target_url: targetUrl } : {}),
  };
  const response = await axiosUnguarded({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: body,
  });
  logger('confirmOtpPinOtp response', response);
  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};

export const validateCurrentPin = async (pinCode: string) => {
  const axios = refreshFetch();
  const endpoint = '/fo/v1/pin_code/validate';
  const response = await axios({
    baseURL: getApiEntrypoint(),
    method: 'post',
    url: endpoint,
    data: { pin_code: pinCode },
  });
  logger('validateCurrentPin response', response);
  const statusData = await statusCodeHelperResponse(response);

  return statusData;
};
